import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardNumberElement } from '@stripe/stripe-js';
import { addPaymentMethod, saveBillingInfo } from '../../../../app/page/subscription/subscription.actions';
import { FormTextInput } from '../../../pages/common/inputs/TextInput';
import {
  billingFieldValue,
  BillingInfoFields,
} from '../../../pages/SettingsPage/Subscription/components/BillingInfoFields';
import { IAddPaymentMethodFormData, IStripeElementFieldProps, SetupFormProps } from './types';

const stripeElementStyle = {
  base: {
    fontSize: '14px',
    color: '#66717c',
    fontFamily: 'inherit',
    letterSpacing: '0.4px',
    '::placeholder': {
      color: '#aab7c4',
    },
  },
  invalid: {
    color: '#fa755a',
  },
};

const stripeFieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 20px',
  background: '#fff',
  borderRadius: '6px',
  border: '2px solid #edf2f5',
  boxSizing: 'border-box',
};

const StripeElementField: FunctionComponent<IStripeElementFieldProps> = ({ children, id }) => (
  <div id={id} className="medium-input" style={stripeFieldStyle}>
    <div style={{ width: '100%' }}>{children}</div>
  </div>
);

export const AddPaymentMethodForm: FunctionComponent<SetupFormProps> = ({
  clientSecret,
  collectBillingInfo,
  onStripeReadyChange,
  onConfirmingChange,
}) => {
  const dispatch = useDispatch();
  // Get the Stripe.js instance injected by the <Elements> provider; used to interact with Stripe APIs.
  const stripe = useStripe();
  // Get the Stripe Elements instance, which manages the UI components for card input.
  const elements = useElements();
  const formProps = useForm<IAddPaymentMethodFormData>({
    defaultValues: {
      nameOnCard: '',
      billingAddressSame: true,
      allowAllAdmins: false,
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
  });
  const [stripeError, setStripeError] = useState<string | null>(null);
  const isConfirmingRef = useRef(false);

  const setConfirming = useCallback(
    (confirming: boolean) => {
      isConfirmingRef.current = confirming;
      onConfirmingChange(confirming);
    },
    [onConfirmingChange],
  );

  useEffect(() => {
    const isReady = Boolean(stripe && elements);
    onStripeReadyChange(isReady);

    return () => {
      onStripeReadyChange(false);
    };
  }, [elements, onStripeReadyChange, stripe]);

  const onSubmit = useCallback(
    async (formData: IAddPaymentMethodFormData) => {
      if (isConfirmingRef.current) {
        return;
      }

      setStripeError(null);

      if (!stripe || !elements) {
        setStripeError('Card form is not ready. Please try again.');
        return;
      }

      const cardElement = elements.getElement(CardNumberElement) as StripeCardNumberElement | null;
      if (!cardElement) {
        setStripeError('Card form is not ready. Please try again.');
        return;
      }

      setConfirming(true);
      try {
        const billingDetails: {
          name?: string;
          address?: { line1: string; city: string; state: string; postal_code: string; country: string };
        } = {
          name: formData.nameOnCard.trim() || undefined,
        };

        if (collectBillingInfo) {
          const address = formData.address.trim();
          const city = formData.city.trim();
          const state = billingFieldValue(formData.state);
          const zipCode = formData.zipCode.trim();
          const country = billingFieldValue(formData.country);
          const saved = await new Promise<boolean>((resolve) => {
            dispatch(
              saveBillingInfo(
                {
                  address,
                  city,
                  state,
                  zipCode,
                  country,
                },
                resolve,
              ),
            );
          });
          if (!saved) {
            return;
          }

          billingDetails.address = {
            line1: address,
            city,
            state,
            postal_code: zipCode,
            country: country === 'United States' ? 'US' : country,
          };
        }

        const result = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: billingDetails,
          },
        });

        if (result.error) {
          setStripeError(result.error.message || 'Unable to confirm the card. Please try again.');
          return;
        }

        const paymentMethodId = result.setupIntent?.payment_method;
        const setupIntentId = result.setupIntent?.id;
        if (!paymentMethodId || typeof paymentMethodId !== 'string') {
          setStripeError('Unable to retrieve the payment method. Please try again.');
          return;
        }

        if (!setupIntentId || typeof setupIntentId !== 'string') {
          setStripeError('Unable to retrieve the setup session. Please try again.');
          return;
        }

        dispatch(
          addPaymentMethod({
            token: paymentMethodId,
            externalSetupRef: setupIntentId,
          }),
        );
      } catch {
        setStripeError('Unable to confirm the card. Please try again.');
      } finally {
        setConfirming(false);
      }
    },
    [clientSecret, collectBillingInfo, dispatch, elements, setConfirming, stripe],
  );

  return (
    <FormProvider {...formProps}>
      <form
        id="add-payment-method-form"
        className="topic-form"
        onSubmit={formProps.handleSubmit(onSubmit)}
        action=""
        method="post"
      >
        <div className="col-12">
          <div className="field-container">
            <label>Credit Card Number</label>
            <StripeElementField id="card-number">
              <CardNumberElement options={{ style: stripeElementStyle }} />
            </StripeElementField>
          </div>
        </div>

        <div className="col-12">
          <div className="flex">
            <div className="pr-1" style={{ flex: 1, minWidth: 0 }}>
              <div className="field-container">
                <label>Expiration Date</label>
                <StripeElementField id="card-expiry">
                  <CardExpiryElement options={{ style: stripeElementStyle, placeholder: 'MM/YY' }} />
                </StripeElementField>
              </div>
            </div>
            <div className="pl-1" style={{ flex: 1, minWidth: 0 }}>
              <div className="field-container">
                <label>CVV2</label>
                <StripeElementField id="card-cvc">
                  <CardCvcElement options={{ style: stripeElementStyle, placeholder: 'Enter' }} />
                </StripeElementField>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="field-container">
            <FormTextInput id="name-on-card" name="nameOnCard" type="text" label="Name on Card" placeholder="Enter" />
          </div>
        </div>
        {collectBillingInfo && <BillingInfoFields />}
        {stripeError && <p style={{ color: 'red' }}>{stripeError}</p>}
      </form>
    </FormProvider>
  );
};
